import { useState, useEffect, useRef, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import GeocodingService, {
  GeocodingResult,
  ReverseGeocodingResult,
} from "../../../api/services/geocoding.service";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { useTranslation } from "react-i18next";
import styles from "./MapLocationPicker.module.scss";

// Fix for default marker icon in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

interface MapLocationPickerProps {
  latitude?: number | null;
  longitude?: number | null;
  address?: string;
  cityName?: string | null;
  countryName?: string | null;
  onLocationChange: (lat: number, lng: number, address: string) => void;
  disabled?: boolean;
}

interface MapClickHandlerProps {
  onMapClick: (lat: number, lng: number) => void;
  disabled?: boolean;
}

// Component to handle map clicks
function MapClickHandler({ onMapClick, disabled }: MapClickHandlerProps) {
  useMapEvents({
    click: (e) => {
      if (!disabled) {
        onMapClick(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
}

// Component to update map view when center/zoom changes
function MapViewUpdater({
  center,
  zoom,
}: {
  center: [number, number];
  zoom: number;
}) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom, { animate: true });
  }, [map, center, zoom]);

  return null;
}

const MapLocationPicker = ({
  latitude,
  longitude,
  address: initialAddress,
  cityName,
  countryName,
  onLocationChange,
  disabled = false,
}: MapLocationPickerProps) => {
  const { t } = useTranslation("translation");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
    address: string;
  } | null>(
    latitude && longitude
      ? {
          lat: latitude,
          lng: longitude,
          address: initialAddress || "",
        }
      : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>(
    latitude && longitude ? [latitude, longitude] : [40.4093, 49.8671], // Default: Baku, Azerbaijan
  );
  const [mapZoom, setMapZoom] = useState(13);
  const [mapBounds, setMapBounds] = useState<
    [[number, number], [number, number]] | null
  >(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [showLocationLinkInput, setShowLocationLinkInput] = useState(false);
  const [locationLinkValue, setLocationLinkValue] = useState("");
  const [isParsingLocation, setIsParsingLocation] = useState(false);
  const locationLinkInputRef = useRef<HTMLInputElement>(null);
  const previousCityRef = useRef<string | null>(null);
  const previousCountryRef = useRef<string | null>(null);

  const debouncedQuery = useDebouncedValue(searchQuery, 500);

  // Update map center and bounds when city changes
  useEffect(() => {
    // Check if city or country actually changed (not just initial mount)
    const currentCity = cityName ?? null;
    const currentCountry = countryName ?? null;
    const cityChanged =
      previousCityRef.current !== null &&
      previousCityRef.current !== currentCity;
    const countryChanged =
      previousCountryRef.current !== null &&
      previousCountryRef.current !== currentCountry;

    // Clear selected location when city or country changes
    if ((cityChanged || countryChanged) && selectedLocation) {
      setSelectedLocation(null);
      onLocationChange(0, 0, "");
    }

    if (cityName) {
      setIsLoading(true);
      GeocodingService.getCityBounds(cityName, countryName || undefined)
        .then((cityData) => {
          if (cityData) {
            setMapBounds(cityData.bounds);
            // Always update map center when city/country changes, or if no location is selected
            if (cityChanged || countryChanged || !selectedLocation) {
              setMapCenter(cityData.center);
              setMapZoom(13);
            }
          }
        })
        .catch((error) => {
          console.error("City bounds error:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setMapBounds(null);
    }

    // Update previous values after processing
    previousCityRef.current = currentCity;
    previousCountryRef.current = currentCountry;
  }, [cityName, countryName, selectedLocation, onLocationChange]);

  // Search for addresses when query changes
  useEffect(() => {
    if (debouncedQuery.trim().length >= 3) {
      setIsLoading(true);
      GeocodingService.searchAddress(
        debouncedQuery,
        5,
        cityName || undefined,
        countryName || undefined,
      )
        .then((results) => {
          setSearchResults(results);
          setShowResults(true);
        })
        .catch((error) => {
          console.error("Search error:", error);
          setSearchResults([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [debouncedQuery, cityName, countryName]);

  // Handle clicking outside search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check if coordinates are within bounds
  const isWithinBounds = useCallback(
    (lat: number, lng: number): boolean => {
      if (!mapBounds) return true;
      const [[minLat, minLng], [maxLat, maxLng]] = mapBounds;
      return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;
    },
    [mapBounds],
  );

  // Handle selecting a search result
  const handleSelectResult = useCallback(
    async (result: GeocodingResult) => {
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);

      if (!isWithinBounds(lat, lng)) {
        return;
      }

      const address = GeocodingService.formatAddress(result);

      setSelectedLocation({ lat, lng, address });
      setMapCenter([lat, lng]);
      setMapZoom(15);
      setSearchQuery("");
      setShowResults(false);
      onLocationChange(lat, lng, address);
    },
    [onLocationChange, isWithinBounds],
  );

  // Handle map click
  const handleMapClick = useCallback(
    async (lat: number, lng: number) => {
      if (!isWithinBounds(lat, lng)) {
        return;
      }

      setIsLoading(true);
      const reverseResult = await GeocodingService.reverseGeocode(lat, lng);

      if (reverseResult) {
        const address = GeocodingService.formatAddress(reverseResult);
        setSelectedLocation({ lat, lng, address });
        onLocationChange(lat, lng, address);
      } else {
        const address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        setSelectedLocation({ lat, lng, address });
        onLocationChange(lat, lng, address);
      }

      setIsLoading(false);
    },
    [onLocationChange, isWithinBounds],
  );

  // Initialize location from props
  useEffect(() => {
    if (latitude && longitude && !selectedLocation) {
      const address = initialAddress || "";
      setSelectedLocation({ lat: latitude, lng: longitude, address });
      setMapCenter([latitude, longitude]);
    }
  }, [latitude, longitude, initialAddress, selectedLocation]);

  // Parse location from various formats
  const parseLocationFromString = useCallback(
    async (input: string): Promise<{ lat: number; lng: number } | null> => {
      const trimmed = input.trim();
      if (!trimmed) return null;

      // 1. Decimal coordinates: "40.409152, 49.956524" or "40.409152,49.956524"
      const decimalMatch = trimmed.match(/^([-\d.]+)\s*,\s*([-\d.]+)$/);
      if (decimalMatch) {
        const lat = parseFloat(decimalMatch[1]);
        const lng = parseFloat(decimalMatch[2]);
        if (
          !isNaN(lat) &&
          !isNaN(lng) &&
          lat >= -90 &&
          lat <= 90 &&
          lng >= -180 &&
          lng <= 180
        ) {
          return { lat, lng };
        }
      }

      // 2. DMS format: "40°24'33.0"N 49°57'23.5"E" or "40°24'33.0\"N 49°57'23.5\"E"
      const dmsMatch = trimmed.match(
        /(\d+)°\s*(\d+)['']\s*([\d.]+)[""]?\s*([NS])\s+(\d+)°\s*(\d+)['']\s*([\d.]+)[""]?\s*([EW])/i,
      );
      if (dmsMatch) {
        const latDeg = parseFloat(dmsMatch[1]);
        const latMin = parseFloat(dmsMatch[2]);
        const latSec = parseFloat(dmsMatch[3]);
        const latDir = dmsMatch[4].toUpperCase();
        const lngDeg = parseFloat(dmsMatch[5]);
        const lngMin = parseFloat(dmsMatch[6]);
        const lngSec = parseFloat(dmsMatch[7]);
        const lngDir = dmsMatch[8].toUpperCase();

        let lat = latDeg + latMin / 60 + latSec / 3600;
        let lng = lngDeg + lngMin / 60 + lngSec / 3600;

        if (latDir === "S") lat = -lat;
        if (lngDir === "W") lng = -lng;

        if (
          !isNaN(lat) &&
          !isNaN(lng) &&
          lat >= -90 &&
          lat <= 90 &&
          lng >= -180 &&
          lng <= 180
        ) {
          return { lat, lng };
        }
      }

      // 3. Google Maps links - various formats
      // Format 1: https://www.google.com/maps?q=40.409152,49.956524
      // Format 2: https://www.google.com/maps/place/.../@40.4091518,49.9539493,17z
      // Format 3: https://maps.google.com/?q=40.409152,49.956524
      // Format 4: https://www.google.com/maps/search/?api=1&query=40.409152,49.956524
      const googleMapsPatterns = [
        /[?&]q=([-\d.]+),([-\d.]+)/,
        /@([-\d.]+),([-\d.]+)/,
        /query=([-\d.]+),([-\d.]+)/,
      ];

      for (const pattern of googleMapsPatterns) {
        const match = trimmed.match(pattern);
        if (match) {
          const lat = parseFloat(match[1]);
          const lng = parseFloat(match[2]);
          if (
            !isNaN(lat) &&
            !isNaN(lng) &&
            lat >= -90 &&
            lat <= 90 &&
            lng >= -180 &&
            lng <= 180
          ) {
            return { lat, lng };
          }
        }
      }

      // 4. Apple Maps links: https://maps.apple.com/?ll=40.409152,49.956524
      const appleMapsMatch = trimmed.match(/[?&]ll=([-\d.]+),([-\d.]+)/);
      if (appleMapsMatch) {
        const lat = parseFloat(appleMapsMatch[1]);
        const lng = parseFloat(appleMapsMatch[2]);
        if (
          !isNaN(lat) &&
          !isNaN(lng) &&
          lat >= -90 &&
          lat <= 90 &&
          lng >= -180 &&
          lng <= 180
        ) {
          return { lat, lng };
        }
      }

      // 5. OpenStreetMap links: https://www.openstreetmap.org/#map=17/40.409152/49.956524
      const osmMatch = trimmed.match(/\/#map=\d+\/([-\d.]+)\/([-\d.]+)/);
      if (osmMatch) {
        const lat = parseFloat(osmMatch[1]);
        const lng = parseFloat(osmMatch[2]);
        if (
          !isNaN(lat) &&
          !isNaN(lng) &&
          lat >= -90 &&
          lat <= 90 &&
          lng >= -180 &&
          lng <= 180
        ) {
          return { lat, lng };
        }
      }

      // 6. Bing Maps links: https://www.bing.com/maps?cp=40.409152~49.956524
      const bingMapsMatch = trimmed.match(/[?&]cp=([-\d.]+)~([-\d.]+)/);
      if (bingMapsMatch) {
        const lat = parseFloat(bingMapsMatch[1]);
        const lng = parseFloat(bingMapsMatch[2]);
        if (
          !isNaN(lat) &&
          !isNaN(lng) &&
          lat >= -90 &&
          lat <= 90 &&
          lng >= -180 &&
          lng <= 180
        ) {
          return { lat, lng };
        }
      }

      // 7. Try to extract any two numbers that look like coordinates
      const anyCoordsMatch = trimmed.match(
        /(-?\d+\.?\d*)\s*[,;]\s*(-?\d+\.?\d*)/,
      );
      if (anyCoordsMatch) {
        const lat = parseFloat(anyCoordsMatch[1]);
        const lng = parseFloat(anyCoordsMatch[2]);
        if (
          !isNaN(lat) &&
          !isNaN(lng) &&
          lat >= -90 &&
          lat <= 90 &&
          lng >= -180 &&
          lng <= 180
        ) {
          return { lat, lng };
        }
      }

      return null;
    },
    [],
  );

  // Handle location link parsing
  const handleParseLocationLink = useCallback(async () => {
    if (!locationLinkValue.trim()) {
      setShowLocationLinkInput(false);
      return;
    }

    setIsParsingLocation(true);
    try {
      const coords = await parseLocationFromString(locationLinkValue);

      if (coords) {
        const { lat, lng } = coords;

        if (!isWithinBounds(lat, lng)) {
          setIsParsingLocation(false);
          return;
        }

        setIsLoading(true);
        try {
          const reverseResult = await GeocodingService.reverseGeocode(lat, lng);

          if (reverseResult) {
            const address = GeocodingService.formatAddress(reverseResult);
            setSelectedLocation({ lat, lng, address });
            setMapCenter([lat, lng]);
            setMapZoom(15);
            onLocationChange(lat, lng, address);
          } else {
            const address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
            setSelectedLocation({ lat, lng, address });
            setMapCenter([lat, lng]);
            setMapZoom(15);
            onLocationChange(lat, lng, address);
          }
        } catch (error) {
          console.error("Reverse geocoding error:", error);
          const address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
          setSelectedLocation({ lat, lng, address });
          setMapCenter([lat, lng]);
          setMapZoom(15);
          onLocationChange(lat, lng, address);
        }

        setIsLoading(false);
        setLocationLinkValue("");
        setShowLocationLinkInput(false);
      } else {
        // Could not parse, show error or try to search
        console.error("Could not parse location from input");
      }
    } catch (error) {
      console.error("Error parsing location:", error);
    } finally {
      setIsParsingLocation(false);
    }
  }, [
    locationLinkValue,
    parseLocationFromString,
    isWithinBounds,
    onLocationChange,
  ]);

  // Focus input when expanded
  useEffect(() => {
    if (showLocationLinkInput && locationLinkInputRef.current) {
      locationLinkInputRef.current.focus();
    }
  }, [showLocationLinkInput]);

  // Handle getting current location
  const handleGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by your browser");
      return;
    }

    setIsGettingLocation(true);

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        navigator.geolocation.clearWatch(watchId);

        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        if (!isWithinBounds(lat, lng)) {
          setIsGettingLocation(false);
          return;
        }

        setIsLoading(true);
        try {
          const reverseResult = await GeocodingService.reverseGeocode(lat, lng);

          if (reverseResult) {
            const address = GeocodingService.formatAddress(reverseResult);
            setSelectedLocation({ lat, lng, address });
            setMapCenter([lat, lng]);
            setMapZoom(15);
            onLocationChange(lat, lng, address);
          } else {
            const address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
            setSelectedLocation({ lat, lng, address });
            setMapCenter([lat, lng]);
            setMapZoom(15);
            onLocationChange(lat, lng, address);
          }
        } catch (error) {
          console.error("Reverse geocoding error:", error);
          const address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
          setSelectedLocation({ lat, lng, address });
          setMapCenter([lat, lng]);
          setMapZoom(15);
          onLocationChange(lat, lng, address);
        }

        setIsLoading(false);
        setIsGettingLocation(false);
      },
      (error) => {
        navigator.geolocation.clearWatch(watchId);
        setIsGettingLocation(false);
        console.error("Geolocation error:", error);
      },
      {
        enableHighAccuracy: false,
        timeout: 30000,
        maximumAge: 60000,
      },
    );

    setTimeout(() => {
      navigator.geolocation.clearWatch(watchId);
      if (isGettingLocation) {
        setIsGettingLocation(false);
      }
    }, 35000);
  }, [isWithinBounds, onLocationChange, isGettingLocation]);

  return (
    <div className={styles.map_picker}>
      <div className={styles.search_container}>
        <div className={styles.search_input_wrapper}>
          <input
            ref={searchInputRef}
            type="text"
            className={styles.search_input}
            placeholder={
              t("map.search_placeholder") || "Search for an address..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (searchResults.length > 0) {
                setShowResults(true);
              }
            }}
            disabled={disabled}
          />
          {isLoading && (
            <div className={styles.loading_indicator}>
              {t("map.searching") || "Searching..."}
            </div>
          )}
          {showResults && searchResults.length > 0 && (
            <div ref={resultsRef} className={styles.search_results}>
              {searchResults.map((result) => (
                <div
                  key={result.place_id}
                  className={styles.result_item}
                  onClick={() => handleSelectResult(result)}
                >
                  <div className={styles.result_name}>
                    {result.display_name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={styles.action_buttons}>
          <button
            type="button"
            className={styles.location_link_btn}
            onClick={() => {
              setShowLocationLinkInput(!showLocationLinkInput);
              if (showLocationLinkInput) {
                setLocationLinkValue("");
              }
            }}
            disabled={disabled}
            title="Add location from link"
          >
            <span className={styles.btn_icon}>🔗</span>
            <span className={styles.btn_text}>
              {t("map.add_location_link") || "Add Location Link"}
            </span>
          </button>
          <button
            type="button"
            className={styles.current_location_btn}
            onClick={handleGetCurrentLocation}
            disabled={disabled || isGettingLocation || isLoading}
            title="Use your current location"
          >
            {isGettingLocation ? (
              <>
                <span className={styles.btn_icon}>📍</span>
                <span className={styles.btn_text}>
                  {t("map.getting_location") || "Getting location..."}
                </span>
              </>
            ) : (
              <>
                <span className={styles.btn_icon}>📍</span>
                <span className={styles.btn_text}>
                  {t("map.use_current_location") || "Use Current Location"}
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {showLocationLinkInput && (
        <div className={styles.location_link_container}>
          <div className={styles.location_link_input_wrapper}>
            <input
              ref={locationLinkInputRef}
              type="text"
              className={styles.location_link_input}
              placeholder={
                t("map.location_link_placeholder") ||
                "Paste location link or coordinates (e.g., 40.409152, 49.956524 or Google Maps link)"
              }
              value={locationLinkValue}
              onChange={(e) => setLocationLinkValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleParseLocationLink();
                } else if (e.key === "Escape") {
                  setShowLocationLinkInput(false);
                  setLocationLinkValue("");
                }
              }}
              disabled={disabled || isParsingLocation}
            />
            {isParsingLocation && (
              <div className={styles.parsing_indicator}>
                {t("map.parsing_location") || "Parsing location..."}
              </div>
            )}
          </div>
          <div className={styles.location_link_actions}>
            <button
              type="button"
              className={styles.parse_btn}
              onClick={handleParseLocationLink}
              disabled={
                disabled || isParsingLocation || !locationLinkValue.trim()
              }
            >
              {t("map.parse_location") || "Parse Location"}
            </button>
            <button
              type="button"
              className={styles.cancel_btn}
              onClick={() => {
                setShowLocationLinkInput(false);
                setLocationLinkValue("");
              }}
              disabled={isParsingLocation}
            >
              {t("buttons.cancel") || "Cancel"}
            </button>
          </div>
        </div>
      )}

      <div className={styles.map_container}>
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
          maxBounds={mapBounds || undefined}
          maxBoundsViscosity={1.0}
          key={`${cityName}-${countryName}`}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapViewUpdater center={mapCenter} zoom={mapZoom} />
          {selectedLocation && (
            <Marker position={[selectedLocation.lat, selectedLocation.lng]} />
          )}
          <MapClickHandler onMapClick={handleMapClick} disabled={disabled} />
        </MapContainer>
      </div>

      {selectedLocation && (
        <div className={styles.selected_info}>
          <div className={styles.info_label}>
            {t("map.selected_location") || "Selected Location:"}
          </div>
          <div className={styles.info_address}>{selectedLocation.address}</div>
          <div className={styles.info_coords}>
            {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapLocationPicker;
